/**
 * Limiter Group Accordion Component
 * Renders a single limiter group as an expandable accordion with checkboxes
 */

import {
  Box,
  Typography,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LimiterGroup } from '../../types';
import ConfidenceScore from '../common/ConfidenceScore';

// Extended limiter type with original index for updates
interface LimiterWithIndex {
  limiter_id: string;
  description: string;
  type: string;
  selected: boolean;
  confidence_score: number;
  reasoning: string;
  originalLimiterIndex: number;
}

// Extended limiter group type with original index and indexed limiters
export interface LimiterGroupWithIndex extends Omit<LimiterGroup, 'limiters'> {
  originalGroupIndex: number;
  limiters: LimiterWithIndex[];
}

interface LimiterGroupAccordionProps {
  group: LimiterGroupWithIndex;
  onGroupChange: (groupIndex: number, selected: boolean) => void;
  onLimiterChange: (groupIndex: number, limiterIndex: number, selected: boolean) => void;
  cleanLimiterDescription: (limiterDescription: string, groupDescription: string) => string;
}

export default function LimiterGroupAccordion({
  group,
  onGroupChange,
  onLimiterChange,
  cleanLimiterDescription,
}: LimiterGroupAccordionProps) {
  return (
    <Accordion
      key={group.limiter_group_id}
      defaultExpanded={false}
      sx={{
        mb: 1,
        bgcolor: group.selected ? 'rgba(99, 102, 241, 0.15)' : 'background.paper',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Checkbox
            checked={group.selected}
            onChange={(e) => {
              e.stopPropagation();
              onGroupChange(group.originalGroupIndex, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <Box sx={{ flex: 1, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{group.description}</Typography>
          </Box>
          {/* Show count of selected limiters */}
          <Chip
            label={`${group.limiters.filter(limiter => limiter.selected).length}/${group.limiters.length}`}
            size="small"
            title={`${group.limiters.filter(limiter => limiter.selected).length} out of ${group.limiters.length} limiters selected in this group`}
            sx={{
              height: '18px',
              fontSize: '0.7rem',
              fontWeight: 500,
              minWidth: 'auto',
              mr: 1,
              bgcolor: group.limiters.filter(limiter => limiter.selected).length === 0 ? 'grey.400' : 'primary.main',
              color: group.limiters.filter(limiter => limiter.selected).length === 0 ? 'grey.600' : 'white',
              opacity: group.limiters.filter(limiter => limiter.selected).length === 0 ? 0.7 : 1,
              '& .MuiChip-label': {
                px: 0.75,
                py: 0,
              },
              cursor: 'help',
            }}
          />
          <ConfidenceScore score={group.confidence_score} reasoning={group.reasoning} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {group.limiters.map((limiter) => (
          <Box
            key={limiter.limiter_id}
            sx={{
              pl: 2,
              py: 1,
              borderLeft: 2,
              borderColor: limiter.selected ? 'primary.main' : 'divider',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Checkbox
                checked={limiter.selected}
                onChange={(e) =>
                  onLimiterChange(group.originalGroupIndex, limiter.originalLimiterIndex, e.target.checked)
                }
              />
              <Box sx={{ flex: 1, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {cleanLimiterDescription(limiter.description, group.description)}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  minWidth: 'fit-content',
                  color: limiter.type ? 'text.secondary' : 'error.main',
                  fontStyle: limiter.type ? 'normal' : 'italic',
                }}
              >
                {limiter.type || '(no type)'}
              </Typography>
              <ConfidenceScore
                score={limiter.confidence_score}
                reasoning={limiter.reasoning}
              />
            </Box>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}
